import React, { useState } from 'react';
import { ChevronDown, ChevronUp, TrendingUp, Footprints, Layers, Trash2, HelpCircle, Repeat, Scale, ShieldCheck } from 'lucide-react';
import { KaizenRule } from '../types';

const RULES: KaizenRule[] = [
  { id: 1, title: "One Small Step", japanese: "改善", description: "Kaizen asks you to break big goals into steps so small they seem ridiculous. 'Floss one tooth', 'Write one sentence'. This bypasses the brain's fear response.", icon: "Footprints" },
  { id: 2, title: "Hansei (Reflection)", japanese: "反省", description: "Honest self-reflection without shame. It is acknowledging a mistake and pledging to improve. Without Hansei, there is no Kaizen.", icon: "Layers" },
  { id: 3, title: "Eliminate Muda (Waste)", japanese: "無駄", description: "Identify and remove waste in your life—whether it's time, energy, or clutter. Simplify to focus on value.", icon: "Trash2" },
  { id: 4, title: "Ask 'Why' 5 Times", japanese: "なぜなぜ分析", description: "To find the root cause of a problem, ask 'Why?' five times. The first answer is rarely the real reason.", icon: "HelpCircle" },
  { id: 5, title: "Consistency Over Intensity", japanese: "継続", description: "Bursts of effort lead to burnout. Small, daily improvements compound into massive change over time. Be the tortoise, not the hare.", icon: "Repeat" },
  { id: 6, title: "Standardize & Improve", japanese: "標準化", description: "Create a standard routine. Once it works, improve it. You cannot improve chaos; you must first stabilize it.", icon: "Scale" },
  { id: 7, title: "Don't Seek Perfection", japanese: "完了", description: "Perfectionism is the enemy of progress. Aim for 'good enough' and then improve it later. Start now.", icon: "ShieldCheck" },
  { id: 8, title: "Kaizen is Infinite", japanese: "無限", description: "There is no finish line. There is always a better way. Enjoy the process of endless becoming.", icon: "TrendingUp" }
];

const IconMap: Record<string, React.ReactNode> = {
  TrendingUp: <TrendingUp size={24} />,
  Footprints: <Footprints size={24} />,
  Layers: <Layers size={24} />,
  Trash2: <Trash2 size={24} />,
  HelpCircle: <HelpCircle size={24} />,
  Repeat: <Repeat size={24} />,
  Scale: <Scale size={24} />,
  ShieldCheck: <ShieldCheck size={24} />,
};

const Kaizen: React.FC = () => {
  const [openId, setOpenId] = useState<number | null>(1);

  const toggle = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <div className="max-w-3xl mx-auto px-6 pb-20 animate-fadeIn">
      <header className="mb-10 text-center">
        <div className="inline-block p-3 bg-green-50 text-green-600 rounded-full mb-4">
          <TrendingUp size={32} />
        </div>
        <h1 className="text-4xl font-serif font-bold text-nunchi-900 mb-3">Kaizen & Hansei</h1>
        <p className="text-xl text-nunchi-500 italic font-serif mb-6">"Good Change through Small Steps"</p>
        <p className="text-nunchi-600 max-w-xl mx-auto leading-relaxed">
          Inspired by Sarah Harvey and Makoto Saito, this philosophy teaches us that giant leaps are scary, 
          but small steps are easy. Transform your life one percent at a time.
        </p>
      </header>

      <div className="space-y-4">
        {RULES.map((rule) => (
          <div 
            key={rule.id} 
            className={`bg-white border rounded-xl overflow-hidden transition-all duration-300 ${openId === rule.id ? 'border-green-400 shadow-md' : 'border-nunchi-200 hover:border-green-300'}`}
          >
            <button 
              onClick={() => toggle(rule.id)}
              className="w-full flex items-center justify-between p-5 text-left focus:outline-none"
            >
              <div className="flex items-center gap-4">
                <div className={`p-2 rounded-lg ${openId === rule.id ? 'bg-green-100 text-green-600' : 'bg-gray-50 text-gray-400'}`}>
                  {IconMap[rule.icon]}
                </div>
                <div>
                  <h3 className={`font-bold text-lg ${openId === rule.id ? 'text-nunchi-900' : 'text-gray-700'}`}>
                    {rule.title}
                  </h3>
                  <p className="text-xs text-nunchi-500 font-serif">{rule.japanese}</p>
                </div>
              </div>
              <div className="text-nunchi-400">
                {openId === rule.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </button>
            
            <div className={`overflow-hidden transition-[max-height] duration-500 ease-in-out ${openId === rule.id ? 'max-h-40' : 'max-h-0'}`}>
              <div className="p-5 pt-0 text-nunchi-600 leading-relaxed border-t border-dashed border-green-200 mt-2">
                {rule.description}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Kaizen;